import { Card, Box, Typography, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { brand } from '../../theme/tokens';

type StatTone = 'gold' | 'sage' | 'navy' | 'rust' | 'neutral';

const toneMap: Record<StatTone, { fg: string; bg: string }> = {
  gold: { fg: brand.goldDark, bg: brand.goldSoft },
  sage: { fg: brand.sage, bg: brand.successSoft },
  navy: { fg: brand.navy, bg: brand.infoSoft },
  rust: { fg: brand.rust, bg: brand.dangerSoft },
  neutral: { fg: brand.inkSoft, bg: '#F4F1ED' },
};

interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  tone?: StatTone;
  hint?: ReactNode;
}

/**
 * Compact KPI tile for dashboards and reports. Emphasises the number and
 * keeps a consistent, friendly look across pages.
 */
const StatCard = ({ label, value, icon, tone = 'gold', hint }: StatCardProps) => {
  const c = toneMap[tone];
  return (
    <Card sx={{ p: { xs: 1.75, sm: 2.25 }, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              color: c.fg,
              bgcolor: c.bg,
              '& svg': { fontSize: 24 },
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {label}
          </Typography>
        </Box>
      </Stack>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {hint}
        </Typography>
      )}
    </Card>
  );
};

export default StatCard;
